import React, { useEffect, useState } from "react";
import { Bot, Plus, Play, Pencil, Trash2, History, ShieldAlert, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { RecipeBuilder } from "@/components/rpa/RecipeBuilder";
import { RunDetail } from "@/components/rpa/RunDetail";

const STATUS = {
  planned: { bg: "rgba(64,96,255,0.12)", fg: "#2f46c9", word: "moves ready" },
  blocked: { bg: "rgba(180,83,9,0.14)", fg: "#B45309", word: "needs your approval" },
  stopped: { bg: "rgba(15,18,38,0.06)", fg: "#5b6076", word: "condition said no" },
  nothing_to_do: { bg: "rgba(15,18,38,0.06)", fg: "#5b6076", word: "nothing to do" },
  failed: { bg: "rgba(220,38,38,0.12)", fg: "#DC2626", word: "failed" },
};

const when = (t) => {
  if (!t) return "";
  if (t.cadence === "hourly") return `every hour at :${String(t.at || "").split(":")[1] || "00"}`;
  if (t.cadence === "weekly") {
    const d = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][t.weekday ?? 1];
    return `${d}s at ${t.at} UTC`;
  }
  return `every day at ${t.at} UTC`;
};

export default function AdminRpa() {
  const [cat, setCat] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState({});
  const [editing, setEditing] = useState(undefined);
  const [runs, setRuns] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/rpa/catalogue"), api.get("/rpa/recipes")]);
      setCat(c.data);
      setRecipes(r.data.recipes || []);
      setSettings(r.data.settings || {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (editing?.id) await api.patch(`/rpa/recipes/${editing.id}`, payload);
      else await api.post("/rpa/recipes", payload);
      toast.success("Saved");
      setEditing(undefined);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runNow = async (rec) => {
    setBusyId(rec.id);
    try {
      const { data } = await api.post(`/rpa/recipes/${rec.id}/run?mode=dry_run`);
      setOpenRun(data.run);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const toggle = async (rec) => {
    try {
      await api.patch(`/rpa/recipes/${rec.id}`, { ...rec, enabled: !rec.enabled });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete "${rec.name}"?`)) return;
    try { await api.delete(`/rpa/recipes/${rec.id}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openHistory = async (rec) => {
    try {
      const { data } = await api.get(`/rpa/runs?recipe_id=${rec.id}&limit=20`);
      setRuns({ recipe: rec, rows: data.runs || [] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const patchSettings = async (patch) => {
    try {
      const { data } = await api.patch("/rpa/settings", patch);
      setSettings(data);
      toast.success(patch.paused !== undefined
        ? (data.paused ? "All automations paused" : "Automations un-paused")
        : (data.members_enabled ? "Members can build automations" : "Members can't build automations"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const signers = (cat?.wallets || []).filter((w) => w.signer?.can_sign).length;

  return (
    <>
      <Header />
      <div className="max-w-[1180px] mx-auto px-5 py-8" data-testid="admin-rpa">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(64,96,255,0.12)", color: "#2f46c9" }}>
              <Bot size={18} />
            </span>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Automations</h1>
          </div>
          <p className="text-sm text-ink-2 mt-2 max-w-[640px]">
            Build a recipe from conditions and actions, give it a time, and it runs itself.
            Each action is one CaryPact call, so you can see exactly what it would send before it sends it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => patchSettings({ members_enabled: !settings.members_enabled })}
            className="btn-secondary" data-testid="rpa-toggle-members" title="Let members build their own automations on their own wallets">
            <Users size={14} /> {settings.members_enabled ? "Members: on" : "Members: off"}
          </button>
          <button onClick={() => patchSettings({ paused: !settings.paused })}
            className="btn-secondary" data-testid="rpa-toggle-pause">
            {settings.paused ? "Un-pause all" : "Pause all"}
          </button>
          <button onClick={() => setEditing(null)} className="btn-primary" data-testid="rpa-new">
            <Plus size={15} /> New automation
          </button>
        </div>
      </div>

      <div className="glass p-4 mb-6 flex items-start gap-3" data-testid="rpa-signer-banner">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" style={{ color: "#B45309" }} />
        <div className="text-sm text-ink-2 leading-relaxed">
          <b style={{ color: "#B45309" }}>These watch and work out the plan — they don't move money yet.</b>
          {" "}CaryPact asks your wallet to approve anything that moves funds, the same tap you get in
          TokenPocket, and that approval only lasts five minutes. Nothing on this page can produce it
          for you{signers === 0 ? "" : ` for ${signers === 1 ? "that one wallet" : `those ${signers} wallets`}`}.
          {" "}So an automation will check the numbers, work out the exact amount, and show you
          <b> exactly what it would do</b> — then wait for you.
          <span className="block mt-1.5 text-[12.5px]" style={{ color: "rgba(15,18,38,0.5)" }}>
            "Needs your approval" on a run means just that: the plan is right, the money hasn't moved.
          </span>
        </div>
      </div>

      {loaded && !recipes.length && (
        <div className="glass p-8 text-center" data-testid="rpa-empty">
          <div className="font-display text-xl font-semibold tracking-tight">Nothing running yet</div>
          <p className="text-sm text-ink-2 mt-2 max-w-[470px] mx-auto">
            The simplest one to start with: <i>every morning, if there's more than 0.02 CA of mining
            revenue waiting, withdraw it into the wallet</i>. Pick it from the list — the steps are
            already filled in.
          </p>
          <button onClick={() => setEditing(null)} className="btn-primary mt-4" data-testid="rpa-new-empty">
            <Plus size={15} /> Build it
          </button>
        </div>
      )}

      <div className="grid gap-3">
        {recipes.map((rec) => {
          const st = STATUS[rec.last_status] || null;
          return (
            <div key={rec.id} className="glass p-4" data-testid={`rpa-recipe-${rec.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-semibold tracking-tight">{rec.name}</span>
                    <span className="chip" style={rec.enabled
                      ? { background: "rgba(15,185,153,0.14)", color: "#067a66" }
                      : { background: "rgba(15,18,38,0.06)", color: "#5b6076" }}>
                      {rec.enabled ? "on" : "off"}
                    </span>
                    {st && <span className="chip" style={{ background: st.bg, color: st.fg }}>{st.word}</span>}
                  </div>
                  <div className="text-[12.5px] text-ink-3 mt-1 inline-flex items-center gap-1.5">
                    <Clock size={12} /> {when(rec.trigger)}
                    {rec.next_run_at && <> · next {String(rec.next_run_at).slice(5, 16).replace("T", " ")}</>}
                  </div>
                  <ol className="mt-2.5 space-y-1">
                    {(rec.summary || []).map((line, i) => (
                      <li key={i} className="text-[13px] text-ink-2 flex gap-2">
                        <span className="text-ink-3 font-mono text-[11px] mt-0.5">{i + 1}</span> {line}
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggle(rec)} className="btn-secondary" data-testid={`rpa-toggle-${rec.id}`}>
                    {rec.enabled ? "Turn off" : "Turn on"}
                  </button>
                  <button onClick={() => runNow(rec)} disabled={busyId === rec.id} className="btn-secondary" data-testid={`rpa-run-${rec.id}`}>
                    <Play size={13} /> {busyId === rec.id ? "Working…" : "Run now"}
                  </button>
                  <button onClick={() => openHistory(rec)} className="btn-secondary" data-testid={`rpa-history-${rec.id}`}>
                    <History size={13} />
                  </button>
                  <button onClick={() => setEditing(rec)} className="btn-secondary" data-testid={`rpa-edit-${rec.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(rec)} className="btn-secondary" data-testid={`rpa-delete-${rec.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing !== undefined && cat && (
        <RecipeBuilder catalogue={cat} recipe={editing} onClose={() => setEditing(undefined)} onSave={save} />
      )}
      {openRun && <RunDetail run={openRun} onClose={() => setOpenRun(null)} />}
      {runs && (
        <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 overflow-y-auto" data-testid="rpa-history">
          <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.45)", backdropFilter: "blur(4px)" }} onClick={() => setRuns(null)} />
          <div className="relative w-full max-w-[620px] my-8 rounded-2xl overflow-hidden fade-up p-6" style={{ background: "white" }}>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-1">{runs.recipe.name}</h3>
            <div className="text-[12.5px] text-ink-3 mb-4">Last {runs.rows.length} run(s)</div>
            {!runs.rows.length && <div className="text-sm text-ink-2">It hasn't run yet.</div>}
            <div className="space-y-2">
              {runs.rows.map((run) => {
                const st = STATUS[run.status] || STATUS.nothing_to_do;
                return (
                  <button key={run.id} onClick={() => { setOpenRun(run); setRuns(null); }}
                    className="w-full text-left glass p-3 hover:bg-black/[0.02] transition-colors"
                    data-testid={`rpa-history-row-${run.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono">{String(run.ran_at).slice(0, 16).replace("T", " ")}</span>
                      <span className="chip" style={{ background: st.bg, color: st.fg }}>{st.word}</span>
                      <span className="text-[12px] text-ink-3">{run.trigger}</span>
                      {run.would_move > 0 && <span className="ml-auto text-sm font-semibold">{run.would_move} CA</span>}
                    </div>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setRuns(null)} className="btn-secondary mt-4" data-testid="rpa-history-close">Close</button>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
